import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import '../button/lib-button';
import '../icon/lib-icon';
import '../progress-bar/lib-progress-bar';
import css from './lib-file-upload.css';

@customElement('lib-file-upload')
export class LibFileUpload extends LibElement {
  protected override get hostFocusSelector(): string { return '.lib-fileupload-field'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];

  @property() headline = '';
  @property() filename = '';
  @property({ type: Boolean }) selected = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) error = false;
  @property() surface = 'white';
  @property({ type: Boolean, attribute: 'show-clear' }) showClear = false;
  @property({ attribute: 'clear-label' }) clearLabel = 'Clear selection';
  @property({ attribute: 'button-text' }) buttonText = 'Upload';
  @property({ attribute: 'button-icon' }) buttonIcon = 'upload';
  @property({ attribute: 'button-icon-url' }) buttonIconUrl = '';
  @property() state = 'idle';
  @property({ type: Number }) progress = 0;
  @property() message = '';
  @property({ attribute: 'uploading-text' }) uploadingText = '';
  @property({ attribute: 'successful-text' }) successfulText = '';
  @property({ type: Boolean }) rejected = false;

  private get inactive() { return this.disabled || this.state === 'uploading'; }

  private openPicker() {
    if (!this.inactive) this.querySelector<HTMLInputElement>('input[type=file]')?.click();
  }

  private clear(event: Event) {
    event.stopPropagation();
    if (this.inactive) return;
    const input = this.querySelector<HTMLInputElement>('input[type=file]');
    if (input) input.value = '';
    this.emit('lib-file-upload-clear');
  }

  private iconUrl(name: string) {
    return `_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/${name}.svg`;
  }

  override render() {
    const classes = ['lib-fileupload', this.surface === 'grey' ? 'lib-fileupload-grey' : '',
      this.error ? 'lib-fileupload-error' : '', this.inactive ? 'lib-fileupload-disabled' : ''].join(' ');
    return html`<div class=${classes}>
      ${this.headline ? html`<div class="lib-fileupload-label">
        <span class="lib-fileupload-label-text">${this.headline}</span><slot name="help"></slot>
      </div>` : nothing}
      <div class="lib-fileupload-container">
        <div class="lib-fileupload-field" role="button" tabindex=${this.inactive ? -1 : 0}
          aria-label=${this.headline || this.buttonText} aria-disabled=${this.inactive}
          @click=${this.openPicker} @keydown=${(event: KeyboardEvent) => {
            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault(); this.openPicker();
            }
          }}>
          <span class=${`lib-fileupload-name ${this.selected ? '' : 'lib-fileupload-name-placeholder'}`}>${this.filename}</span>
          ${this.showClear && !this.inactive ? html`<button type="button" class="lib-fileupload-action lib-fileupload-clear"
            aria-label=${this.clearLabel} @click=${this.clear}><lib-icon icon-url=${this.iconUrl('close')}></lib-icon></button>` : nothing}
          ${this.error ? html`<lib-icon class="lib-fileupload-warning" icon-url=${this.iconUrl('warning')}></lib-icon>` : nothing}
        </div>
        <lib-button ?disabled=${this.inactive} @click=${this.openPicker}>
          ${this.buttonIcon || this.buttonIconUrl ? html`<lib-icon icon-url=${this.buttonIconUrl || this.iconUrl(this.buttonIcon)}></lib-icon>` : nothing}
          ${this.buttonText}
        </lib-button>
      </div>
      ${this.renderStatus()}
      <slot name="picker"></slot>
    </div>`;
  }

  private renderStatus() {
    if (this.rejected) return html`<div class="lib-fileupload-status lib-fileupload-status-failed" role="alert">
      <span class="lib-fileupload-status-text">${this.message}</span></div>`;
    if (this.state === 'uploading') return html`<div class="lib-fileupload-status"><lib-progress-bar
      current=${this.progress} message=${this.uploadingText} icon-name="spinner"></lib-progress-bar></div>`;
    if (this.state === 'successful') return html`<div class="lib-fileupload-status lib-fileupload-status-successful">
      <lib-icon icon-url=${this.iconUrl('tick-sign')}></lib-icon><span class="lib-fileupload-status-text">${this.successfulText}</span></div>`;
    if (this.message) return html`<div class="lib-fileupload-status lib-fileupload-status-failed">
      <span class="lib-fileupload-status-text">${this.message}</span></div>`;
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-file-upload': LibFileUpload; }
}
